<template>
  <div>
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" label="Loading..." />
      <p class="mt-2 text-muted">جاري تحميل بيانات النشاط...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-2" />
      <h4>حدث خطأ في تحميل النشاط</h4>
      <p class="text-muted">{{ error }}</p>
      <b-button variant="primary" @click="$router.go(-1)">
        <feather-icon icon="ArrowLeftIcon" class="mr-50" />
        العودة للأنشطة
      </b-button>
    </div>

    <!-- Activity Content -->
    <div v-else-if="activity">
      <!-- Header Card -->
      <b-card class="mb-2">
        <b-row align-v="center">
          <b-col cols="12" md="8">
            <div class="d-flex align-items-center mb-1">
              <b-badge :variant="getActivityVariant(activity.activity_type)" pill class="mr-1">
                {{ activity.activity_type }}
              </b-badge>
              <b-badge variant="light-secondary" pill>
                {{ activity.code }}
              </b-badge>
            </div>
            <h3 class="mb-1">{{ activity.name }}</h3>
            <div class="text-muted">
              <feather-icon icon="BookOpenIcon" size="16" class="mr-50" />
              <span>{{ activity.subject_code }}</span>
              <span class="mx-1">|</span>
              <feather-icon icon="AwardIcon" size="16" class="mr-50" />
              <span>{{ activity.eductional_level }}</span>
            </div>
          </b-col>
          <b-col cols="12" md="4" class="text-md-right mt-1 mt-md-0">
            <b-button variant="outline-secondary" @click="$router.go(-1)">
              <feather-icon icon="ArrowLeftIcon" class="mr-50" />
              العودة
            </b-button>
          </b-col>
        </b-row>
      </b-card>

      <!-- Dates Info -->
      <b-card class="mb-2">
        <b-row>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center">
              <feather-icon icon="CalendarIcon" size="20" class="text-success mr-1" />
              <div>
                <small class="text-muted d-block">تاريخ البداية</small>
                <strong>{{ formatDate(activity.from_date) }}</strong>
              </div>
            </div>
          </b-col>
          <b-col cols="12" md="6" class="mt-1 mt-md-0">
            <div class="d-flex align-items-center">
              <feather-icon icon="CalendarIcon" size="20" class="text-danger mr-1" />
              <div>
                <small class="text-muted d-block">تاريخ النهاية</small>
                <strong>{{ formatDate(activity.to_date) }}</strong>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Activity Details Based on Type -->
      
      <!-- Homework/Project Details -->
      <b-card v-if="activity.assignment_and_projects_data" class="mb-2">
        <h5 class="mb-2">تفاصيل الواجب</h5>
        <b-row>
          <b-col cols="12" md="6">
            <p><strong>نوع المحتوى:</strong> {{ activity.assignment_and_projects_data.type }}</p>
            <p><strong>حجم الملف المسموح:</strong> {{ activity.assignment_and_projects_data.file_size }}</p>
          </b-col>
          <b-col cols="12" md="6">
            <p><strong>عدد الملفات المسموحة:</strong> {{ activity.assignment_and_projects_data.max_files_allowed_count }}</p>
            <p><strong>السماح بإعادة التسليم:</strong> 
              <b-badge :variant="activity.assignment_and_projects_data.allow_resubmit ? 'success' : 'danger'">
                {{ activity.assignment_and_projects_data.allow_resubmit ? 'نعم' : 'لا' }}
              </b-badge>
            </p>
            <p><strong>السماح بالتسليم المتأخر:</strong> 
              <b-badge :variant="activity.assignment_and_projects_data.disallow_delayed_homeworks ? 'danger' : 'success'">
                {{ activity.assignment_and_projects_data.disallow_delayed_homeworks ? 'لا' : 'نعم' }}
              </b-badge>
            </p>
          </b-col>
        </b-row>
        <hr>
        <h6>الوصف:</h6>
        <p class="mb-0">{{ activity.assignment_and_projects_data.description }}</p>
      </b-card>

      <!-- Forum Details -->
      <b-card v-if="activity.forums_data" class="mb-2">
        <h5 class="mb-2">تفاصيل المنتدى</h5>
        <b-row>
          <b-col cols="12" md="6">
            <p><strong>العنوان:</strong> {{ activity.forums_data.subject }}</p>
            <p><strong>نوع التقييم:</strong> {{ activity.forums_data.rate_type }}</p>
          </b-col>
          <b-col cols="12" md="6">
            <p><strong>حجم الملف المسموح:</strong> {{ activity.forums_data.file_size }}</p>
            <p><strong>عدد الملفات المسموحة:</strong> {{ activity.forums_data.max_files_allowed_count }}</p>
          </b-col>
        </b-row>
        <hr>
        <h6>المناقشة:</h6>
        <p class="mb-0">{{ activity.forums_data.discussion }}</p>
      </b-card>

      <!-- Exam Details -->
      <b-card v-if="activity.short_exams_data" class="mb-2">
        <h5 class="mb-2">تفاصيل الامتحان القصير</h5>
        <b-row>
          <b-col cols="12" md="6">
            <p v-if="activity.short_exams_data.allowed_periods">
              <strong>المدة:</strong> {{ activity.short_exams_data.period }} {{ activity.short_exams_data.period_type }}
            </p>
            <p><strong>عدد المحاولات المسموحة:</strong> {{ activity.short_exams_data.attemps }}</p>
          </b-col>
          <b-col cols="12" md="6">
            <p><strong>نوع التقييم:</strong> {{ activity.short_exams_data.rate_type }}</p>
          </b-col>
        </b-row>

        <!-- Questions -->
        <div v-if="activity.short_exams_data.questions && activity.short_exams_data.questions.length > 0">
          <hr>
          <p><strong>عدد الأسئلة:</strong> {{ activity.short_exams_data.questions.length }}</p>
        </div>
      </b-card>

      <!-- Previous Attempts -->
      <b-card class="mb-2">
        <h5 class="mb-2">المحاولات السابقة</h5>
        <p class="text-muted mb-2" v-if="maxAttempts">
          <strong>عدد المحاولات:</strong> {{ totalAttempts }} / {{ maxAttempts }}
        </p>
        <p class="text-muted mb-2" v-else>
          <strong>عدد المحاولات:</strong> {{ totalAttempts }}
        </p>
        
        <div v-if="loadingAttempts" class="text-center py-3">
          <b-spinner variant="primary" />
          <p class="text-muted mt-2 mb-0">جاري تحميل المحاولات...</p>
        </div>
        
        <b-table
          v-else-if="attempts && attempts.length > 0"
          :items="attempts"
          :fields="attemptsFields"
          responsive
          striped
          hover>
          <template #cell(attempt_number)="data">
            <b-badge variant="primary">{{ data.value }}</b-badge>
          </template>
          <!-- Forum-specific cells -->
          <template #cell(subject)="data">
            <span v-if="data.value">{{ data.value }}</span>
            <span v-else class="text-muted">-</span>
          </template>
          
          <!-- Homework-specific cells -->
          <template #cell(text_preview)="data">
            <span v-if="data.value" class="text-truncate d-inline-block" style="max-width: 200px;">
              {{ data.value }}
            </span>
            <span v-else class="text-muted">-</span>
          </template>
          
          <!-- Exam-specific cells -->
          <template #cell(score)="data">
            <span v-if="data.value">{{ data.value }}</span>
            <span v-else class="text-muted">-</span>
          </template>
          
          <!-- Common cells -->
          <template #cell(files_count)="data">
            <b-badge variant="info" v-if="data.value > 0">
              <feather-icon icon="PaperclipIcon" size="14" class="mr-25" />
              {{ data.value }}
            </b-badge>
            <span v-else class="text-muted">-</span>
          </template>
          
          <template #cell(grade)="data">
            <b-badge :variant="data.value ? 'success' : 'secondary'">
              {{ data.value || '-' }}
            </b-badge>
          </template>
          
          <template #cell(submitted_at)="data">
            {{ formatDate(data.value) }}
          </template>
          
          <template #cell(actions)="data">
            <b-button size="sm" variant="outline-info" @click="viewAttempt(data.item)">
              <feather-icon icon="EyeIcon" size="16" class="mr-25" />
              عرض
            </b-button>
          </template>
        </b-table>

        <b-alert v-else-if="!loadingAttempts" show variant="info" class="mb-0">
          <div class="alert-body text-center">
            <feather-icon icon="InfoIcon" class="mr-50" />
            <span>لم تقم بأي محاولات لهذا النشاط بعد</span>
          </div>
        </b-alert>
      </b-card>

      <!-- Attempt Detail Modal -->
      <b-modal
        v-model="showAttemptModal"
        size="lg"
        :title="'تفاصيل المحاولة #' + (selectedAttempt ? selectedAttempt.attempt_number : '')"
        hide-footer
        scrollable>
        <div v-if="selectedAttempt && selectedAttempt.raw_data">
          <!-- Forum Attempt -->
          <div v-if="selectedAttempt.submission_type === 'forum'">
            <b-card class="mb-2">
              <h6><strong>العنوان:</strong></h6>
              <p>{{ selectedAttempt.raw_data.subject }}</p>
              <hr>
              <h6><strong>المحتوى:</strong></h6>
              <div v-html="selectedAttempt.raw_data.description"></div>
            </b-card>

            <!-- Files -->
            <b-card v-if="selectedAttempt.raw_data.files && selectedAttempt.raw_data.files.length > 0">
              <h6 class="mb-2">الملفات المرفقة ({{ selectedAttempt.raw_data.files.length }})</h6>
              <b-list-group>
                <b-list-group-item
                  v-for="file in selectedAttempt.raw_data.files"
                  :key="file.id"
                  class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon icon="FileIcon" class="mr-50" />
                    <span>{{ file.original_name }}</span>
                    <small class="text-muted ml-1">({{ formatFileSize(file.file_size) }})</small>
                  </div>
                  <b-button
                    size="sm"
                    variant="primary"
                    :href="'/' + file.file_path"
                    target="_blank">
                    <feather-icon icon="DownloadIcon" size="14" class="mr-25" />
                    تحميل
                  </b-button>
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </div>

          <!-- Homework/Project Attempt -->
          <div v-else-if="selectedAttempt.submission_type === 'homework' || selectedAttempt.submission_type === 'project'">
            <b-card class="mb-2">
              <h6><strong>الإجابة النصية:</strong></h6>
              <div v-html="selectedAttempt.raw_data.text_answer || selectedAttempt.raw_data.description || '-'"></div>
            </b-card>

            <!-- Grade -->
            <b-alert v-if="selectedAttempt.raw_data.grade" show variant="success">
              <strong>الدرجة:</strong> {{ selectedAttempt.raw_data.grade }}
            </b-alert>

            <!-- Files -->
            <b-card v-if="selectedAttempt.raw_data.files && selectedAttempt.raw_data.files.length > 0">
              <h6 class="mb-2">الملفات المرفقة ({{ selectedAttempt.raw_data.files.length }})</h6>
              <b-list-group>
                <b-list-group-item
                  v-for="file in selectedAttempt.raw_data.files"
                  :key="file.id"
                  class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon icon="FileIcon" class="mr-50" />
                    <span>{{ file.original_name }}</span>
                    <small class="text-muted ml-1">({{ formatFileSize(file.file_size) }})</small>
                  </div>
                  <b-button
                    size="sm"
                    variant="primary"
                    :href="'/' + file.file_path"
                    target="_blank">
                    <feather-icon icon="DownloadIcon" size="14" class="mr-25" />
                    تحميل
                  </b-button>
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </div>

          <!-- Exam Attempt -->
          <div v-else-if="selectedAttempt.submission_type === 'exam' || selectedAttempt.submission_type === 'short_exam'">
            <!-- Total Mark -->
            <b-alert show variant="info" class="p-2">
              <div class="d-flex justify-content-between align-items-center">
                <span><strong>إجمالي الدرجات:</strong></span>
                <span class="h5 mb-0">
                  {{ selectedAttempt.raw_data.total_mark || 0 }} / {{ calculateTotalPossibleMarks(selectedAttempt.raw_data.answers) }}
                </span>
              </div>
            </b-alert>

            <!-- Questions and Answers -->
            <b-card v-if="selectedAttempt.raw_data.answers && selectedAttempt.raw_data.answers.length > 0">
              <h6 class="mb-3">الأسئلة والإجابات ({{ selectedAttempt.raw_data.answers.length }})</h6>
              
              <div v-for="(answer, index) in selectedAttempt.raw_data.answers" :key="answer.id" class="mb-3 pb-3 border-bottom">
                <!-- Question Header -->
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div class="flex-grow-1">
                    <b-badge :variant="answer.is_correct === 1 ? 'success' : answer.is_correct === 0 ? 'danger' : 'secondary'" class="mr-1">
                      {{ index + 1 }}
                    </b-badge>
                    <strong>نوع السؤال:</strong> {{ answer.question_type }}
                  </div>
                  <b-badge :variant="answer.is_correct === 1 ? 'success' : answer.is_correct === 0 ? 'danger' : 'warning'">
                    {{ answer.mark_awarded }} / {{ answer.total_mark }}
                  </b-badge>
                </div>

                <!-- Student Answer Based on Question Type -->
                <div class="pr-4">
                  <!-- True/False or Multiple Choice Single -->
                  <div v-if="answer.answer_id && !answer.choices.length">
                    <p class="mb-1 text-muted">الإجابة:</p>
                    <p v-if="answer.question_type === 'صح و خطأ'" class="mb-0">
                      {{ answer.answer_id === 1 ? 'صح' : 'خطأ' }}
                    </p>
                    <p v-else class="mb-0">{{ answer.answer_id }}</p>
                  </div>

                  <!-- Multiple Choice Multiple Answers -->
                  <div v-else-if="answer.choices && answer.choices.length > 0 && answer.question_type.includes('متعدد الأختيارات و اجابات')">
                    <p class="mb-1 text-muted">الإجابات المختارة:</p>
                    <b-list-group>
                      <b-list-group-item v-for="choice in answer.choices" :key="choice.id">
                        {{ choice.answer_details ? choice.answer_details.answer : choice.answer_id }}
                      </b-list-group-item>
                    </b-list-group>
                  </div>

                  <!-- Ordering Questions -->
                  <div v-else-if="answer.choices && answer.choices.length > 0 && answer.question_type === 'ترتيب الأجابات'">
                    <p class="mb-1 text-muted">الترتيب المختار:</p>
                    <b-list-group>
                      <b-list-group-item 
                        v-for="choice in answer.choices" 
                        :key="choice.id"
                        class="d-flex align-items-center">
                        <b-badge variant="primary" class="mr-2">{{ choice.order_position }}</b-badge>
                        <span>{{ choice.answer_details ? choice.answer_details.answer : choice.answer_id }}</span>
                      </b-list-group-item>
                    </b-list-group>
                  </div>

                  <!-- Text-based Answers -->
                  <div v-else-if="answer.text_answer">
                    <p class="mb-1 text-muted">الإجابة:</p>
                    <div v-if="['مقالي طويل', 'مقالي قصير', 'مقالي مركب'].includes(answer.question_type)" 
                         v-html="answer.text_answer" 
                         class="border p-2 rounded bg-light">
                    </div>
                    <p v-else class="border p-2 rounded bg-light mb-0">{{ answer.text_answer }}</p>
                  </div>

                  <!-- No Answer -->
                  <div v-else>
                    <p class="text-muted mb-0"><em>لم يتم الإجابة على هذا السؤال</em></p>
                  </div>

                  <!-- Correctness Indicator -->
                  <div class="mt-2">
                    <b-badge v-if="answer.is_correct === 1" variant="success">
                      <feather-icon icon="CheckCircleIcon" size="14" class="mr-25" />
                      إجابة صحيحة
                    </b-badge>
                    <b-badge v-else-if="answer.is_correct === 0" variant="danger">
                      <feather-icon icon="XCircleIcon" size="14" class="mr-25" />
                      إجابة خاطئة
                    </b-badge>
                    <b-badge v-else variant="warning">
                      <feather-icon icon="AlertCircleIcon" size="14" class="mr-25" />
                      قيد التصحيح
                    </b-badge>
                  </div>
                </div>
              </div>
            </b-card>
          </div>

          <!-- Submission Date -->
          <p class="text-muted mt-2 mb-0">
            <small>تاريخ التقديم: {{ formatDate(selectedAttempt.submitted_at) }}</small>
          </p>
        </div>
      </b-modal>

      <!-- Action Buttons -->
      <b-card>
        <b-alert v-if="!canStartActivity && disableReason" show variant="warning" class="mb-2">
          <div class="alert-body text-center">
            <feather-icon icon="AlertCircleIcon" class="mr-50" />
            <span>{{ disableReason }}</span>
          </div>
        </b-alert>
        
        <b-row>
          <b-col cols="12" md="6">
            <b-button 
              variant="primary" 
              block 
              size="lg"
              :disabled="!canStartActivity"
              @click="startActivity">
              <feather-icon icon="EditIcon" class="mr-50" />
              بدء النشاط
            </b-button>
          </b-col>
          <b-col cols="12" md="6" class="mt-1 mt-md-0">
            <b-button variant="outline-secondary" block size="lg" @click="$router.go(-1)">
              <feather-icon icon="ArrowLeftIcon" class="mr-50" />
              العودة للأنشطة
            </b-button>
          </b-col>
        </b-row>
      </b-card>
    </div>
  </div>
</template>

<script>
import ActivitiesController from "@/Controllers/ActivitiesController";
import Auth from "@/Apis/Auth";

export default {
  data() {
    return {
      activity: null,
      loading: true,
      error: null,
      attempts: [],
      totalAttempts: 0,
      loadingAttempts: true,
      showAttemptModal: false,
      selectedAttempt: null,
    };
  },
  computed: {
    attemptsFields() {
      // Common fields for all types
      const fields = [
        { key: 'attempt_number', label: 'المحاولة', thClass: 'text-center', tdClass: 'text-center' },
      ];

      // Add type-specific fields
      if (this.activity) {
        if (this.activity.forums_data) {
          // Forum fields
          fields.push({ key: 'subject', label: 'العنوان' });
          fields.push({ key: 'files_count', label: 'الملفات', thClass: 'text-center', tdClass: 'text-center' });
        } else if (this.activity.assignment_and_projects_data) {
          // Homework/Project fields
          fields.push({ key: 'text_preview', label: 'الإجابة' });
          fields.push({ key: 'files_count', label: 'الملفات', thClass: 'text-center', tdClass: 'text-center' });
          fields.push({ key: 'grade', label: 'الدرجة', thClass: 'text-center', tdClass: 'text-center' });
        } else if (this.activity.short_exams_data) {
          // Exam fields
          fields.push({ key: 'score', label: 'النتيجة', thClass: 'text-center', tdClass: 'text-center' });
          fields.push({ key: 'grade', label: 'الدرجة', thClass: 'text-center', tdClass: 'text-center' });
        }
      }

      // Common fields for all types
      fields.push({ key: 'submitted_at', label: 'تاريخ التسليم' });
      fields.push({ key: 'actions', label: 'الإجراءات', thClass: 'text-center', tdClass: 'text-center' });

      return fields;
    },
    
    maxAttempts() {
      // Only exams have attempt limits
      if (this.activity && this.activity.short_exams_data) {
        return this.activity.short_exams_data.attemps || 3;
      }
      return null; // No limit for homework/forum
    },
    
    isWithinDateRange() {
      // Check if current date is within activity date range
      if (!this.activity) return false;
      
      const now = new Date();
      const startDate = new Date(this.activity.from_date);
      const endDate = new Date(this.activity.to_date);
      
      // Check if started
      if (now < startDate) {
        return false;
      }
      
      // For homework/project, check disallow_delayed_homeworks setting
      if (this.activity.assignment_and_projects_data) {
        const disallowLateSubmission = this.activity.assignment_and_projects_data.disallow_delayed_homeworks;
        // If disallow_delayed_homeworks is 1, don't allow submissions after end date
        if (disallowLateSubmission === 1 && now > endDate) {
          return false;
        }
        // If disallow_delayed_homeworks is 0, allow late submissions
        return true;
      }
      
      // For other types (forum, exam), check end date normally
      return now <= endDate;
    },
    
    canStartActivity() {
      // Check date range first
      if (!this.isWithinDateRange) {
        return false;
      }
      
      // For exams, check attempt limit
      if (this.activity && this.activity.short_exams_data) {
        return this.totalAttempts < this.maxAttempts;
      }
      
      // For homework/project, check allow_resubmit setting
      if (this.activity && this.activity.assignment_and_projects_data) {
        const allowResubmit = this.activity.assignment_and_projects_data.allow_resubmit;
        // If allow_resubmit is 0 and student already submitted, don't allow resubmission
        if (allowResubmit === 0 && this.totalAttempts > 0) {
          return false;
        }
      }
      
      // For forum, only check date range
      return true;
    },
    
    disableReason() {
      // Determine why the button is disabled
      if (!this.isWithinDateRange) {
        const now = new Date();
        const startDate = new Date(this.activity.from_date);
        const endDate = new Date(this.activity.to_date);
        
        if (now < startDate) {
          return 'لم يبدأ موعد النشاط بعد';
        } else if (now > endDate) {
          // For homework, check if late submission is disallowed
          if (this.activity && this.activity.assignment_and_projects_data) {
            const disallowLateSubmission = this.activity.assignment_and_projects_data.disallow_delayed_homeworks;
            if (disallowLateSubmission === 1) {
              return 'انتهى موعد النشاط ولا يسمح بالتسليم المتأخر';
            }
          }
          return 'انتهى موعد النشاط';
        }
      }
      
      // For exams, check attempt limit
      if (this.activity && this.activity.short_exams_data && this.totalAttempts >= this.maxAttempts) {
        return `لقد استنفدت جميع المحاولات المسموحة (${this.maxAttempts})`;
      }
      
      // For homework, check resubmission
      if (this.activity && this.activity.assignment_and_projects_data) {
        const allowResubmit = this.activity.assignment_and_projects_data.allow_resubmit;
        if (allowResubmit === 0 && this.totalAttempts > 0) {
          return 'لقد قمت بالتسليم ولا يسمح بإعادة التسليم';
        }
      }
      
      return '';
    },
  },
  async created() {
    await this.fetchActivity();
    await this.fetchAttempts();
  },
  methods: {
    async fetchActivity() {
      try {
        this.loading = true;
        this.error = null;

        const activityId = this.$route.params.id;
        if (!activityId) {
          this.error = "معرّف النشاط غير موجود";
          return;
        }

        const activitiesController = new ActivitiesController();
        const result = await activitiesController.getActivityForStudent(activityId);

        this.activity = result;
        
        // Set page title
        if (this.activity && this.activity.name) {
          this.$mobileTitle.label = this.activity.name;
        }
      } catch (error) {
        this.error = "حدث خطأ في تحميل بيانات النشاط";
      } finally {
        this.loading = false;
      }
    },

    async fetchAttempts() {
      try {
        this.loadingAttempts = true;
        const activityId = this.$route.params.id;
        const user = Auth.USER;
        
        if (!activityId || !user || !user.id) {
          this.loadingAttempts = false;
          return;
        }

        const activitiesController = new ActivitiesController();
        const result = await activitiesController.getActivityAttempt(activityId, user.id);

        if (result && result.success && result.submissions) {
          this.totalAttempts = result.total_attempts || result.submissions.length;
          
          // Map submissions to table format
          this.attempts = result.submissions.map((submission, index) => {
            const tableRow = {
              attempt_number: index + 1,
              submitted_at: submission.submitted_at,
              submission_type: submission.submission_type,
              student_submission_id: submission.student_submission_id,
              raw_data: submission.submission_data,
            };

            // Add type-specific fields
            if (submission.submission_type === 'forum' && submission.submission_data) {
              // Forum submission
              tableRow.subject = submission.submission_data.subject || '-';
              tableRow.files_count = submission.submission_data.files ? submission.submission_data.files.length : 0;
            } else if (submission.submission_type === 'homework' || submission.submission_type === 'project') {
              // Homework/Project submission
              const textAnswer = submission.submission_data.text_answer || submission.submission_data.description || '';
              tableRow.text_preview = this.stripHtml(textAnswer).substring(0, 50) + '...';
              tableRow.files_count = submission.submission_data.files ? submission.submission_data.files.length : 0;
              tableRow.grade = submission.submission_data.grade || null;
            } else if (submission.submission_type === 'exam' || submission.submission_type === 'short_exam') {
              // Exam submission
              tableRow.score = submission.submission_data.total_mark || submission.submission_data.score || '-';
              tableRow.grade = submission.submission_data.grade || null;
            }

            return tableRow;
          });
        } else {
          this.attempts = [];
          this.totalAttempts = 0;
        }
      } catch (error) {
        // Handle error silently, attempts table will show "no attempts" message
        this.attempts = [];
        this.totalAttempts = 0;
      } finally {
        this.loadingAttempts = false;
      }
    },

    viewAttempt(attempt) {
      this.selectedAttempt = attempt;
      this.showAttemptModal = true;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },

    stripHtml(html) {
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    },

    calculateTotalPossibleMarks(answers) {
      if (!answers || !Array.isArray(answers)) return 0;
      return answers.reduce((total, answer) => total + (answer.total_mark || 0), 0);
    },

    startActivity() {
      this.$router.push({ 
        name: 'activities-v2-attend', 
        params: { id: this.activity.id } 
      });
    },

    getActivityVariant(activityType) {
      const variants = {
        "واجب": "success",
        "منتدي": "info",
        "امتحان قصير": "danger",
        "بحث قصير": "warning",
        "مشروع": "primary",
      };
      return variants[activityType] || "secondary";
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("ar-EG", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.rtl-popup {
  text-align: right;
  direction: rtl;
}
</style>

